// ── Welle D3c: „Schulfreie Tage deiner Schule“ (Einstellungen) ──
// Bewegliche Ferientage, pädagogische Tage … (D3a: /api/schulfreie-tage). LehrerDesk zählt sie
// nicht als Schultag — Heute („Dein Tag“, Countdown), Woche und „Offene Klassenbuch-Einträge“
// werden danach neu geladen.
import React, { useEffect, useState } from 'react';
import { apiDelete, apiGet, apiPost, zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';
import { MIcon } from '../../lib/micon';
import { LdFeld, LdEingabe, LdDatum, LdChips } from '../../components/ld/LdFenster';
import { IconButton } from '../../components/ld/IconButton';
import { ladeNeu } from '../heute/widgets/lader';
import { DEIN_TAG_PFAD } from '../heute/widgets/DeinTag';
import './unterrichtszeiten.css';

type Tag = { id: number; datum: string; bezeichnung: string };
const VORSCHLAEGE = ['Beweglicher Ferientag', 'Pädagogischer Tag'];

function heuteNeuLaden() {
  for (const p of [DEIN_TAG_PFAD, '/api/heute/woche', '/api/heute/klassenbuch-offen']) void ladeNeu(p);
}
const lang = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

export function SchulfreieTage() {
  const [tage, setTage] = useState<Tag[] | null>(null);
  const [fehler, setFehler] = useState(false);
  const [offen, setOffen] = useState(false);
  const [datum, setDatum] = useState('');
  const [bezeichnung, setBezeichnung] = useState(VORSCHLAEGE[0]);
  const [speichert, setSpeichert] = useState(false);

  function laden() {
    setFehler(false);
    apiGet<Tag[]>('/api/schulfreie-tage').then(setTage).catch(() => setFehler(true));
  }
  useEffect(laden, []);

  async function hinzufuegen() {
    if (!datum || !bezeichnung.trim()) return;
    setSpeichert(true);
    try {
      const neu = await apiPost<Tag>('/api/schulfreie-tage', { datum, bezeichnung: bezeichnung.trim() });
      setTage(prev => [...(prev ?? []), neu].sort((a, b) => a.datum.localeCompare(b.datum)));
      setDatum(''); setOffen(false);
      toast('Schulfreier Tag eingetragen');
      heuteNeuLaden();
    } catch (e) { zeigeApiFehler(e, 'Eintragen fehlgeschlagen'); } finally { setSpeichert(false); }
  }

  async function loeschen(t: Tag) {
    try {
      await apiDelete(`/api/schulfreie-tage/${t.id}`);
      setTage(prev => (prev ?? []).filter(x => x.id !== t.id));
      heuteNeuLaden();
      toast(`${t.bezeichnung} entfernt`, 'info', {
        dauerMs: 6000,
        aktion: {
          label: 'Rückgängig',
          onClick: async () => {
            try {
              const neu = await apiPost<Tag>('/api/schulfreie-tage', { datum: t.datum, bezeichnung: t.bezeichnung });
              setTage(prev => [...(prev ?? []), neu].sort((a, b) => a.datum.localeCompare(b.datum)));
              heuteNeuLaden();
            } catch (e) { zeigeApiFehler(e, 'Wiederherstellen fehlgeschlagen'); }
          },
        },
      });
    } catch (e) { zeigeApiFehler(e, 'Entfernen fehlgeschlagen'); }
  }

  const heute = new Date().toLocaleDateString('sv-SE');
  return (
    <div id="schulfreie-tage" className="ld-zr">
      {fehler ? (
        <div className="ld-sft__fehler">Konnte nicht geladen werden. <button type="button" className="ld-zr__pause-plus" onClick={laden}>Erneut versuchen</button></div>
      ) : tage === null ? (
        <p className="ld-zr__text">Lädt …</p>
      ) : tage.length === 0 ? (
        <p className="ld-sft__leer">Noch keine Tage eingetragen.</p>
      ) : (
        <ul className="ld-sft__liste">
          {tage.map(t => (
            <li key={t.id} className={`ld-sft__tag${t.datum < heute ? ' ist-vorbei' : ''}`}>
              <span className="ld-sft__text"><b>{t.bezeichnung}</b><small>{lang(t.datum)}</small></span>
              <IconButton variant="bare" size={36} label={`${t.bezeichnung} am ${lang(t.datum)} entfernen`} onClick={() => loeschen(t)}>
                <MIcon name="delete" size={18} />
              </IconButton>
            </li>
          ))}
        </ul>
      )}

      {offen ? (
        <div className="ld-zr__schnell">
          <div className="ld-zr__schnell-grid ld-sft__form">
            <LdFeld label="Datum">
              <LdDatum aria-label="Datum" value={datum} onChange={e => setDatum(e.target.value)} />
            </LdFeld>
            <LdFeld label="Bezeichnung" htmlFor="ld-sft-bez">
              <LdEingabe id="ld-sft-bez" value={bezeichnung} maxLength={100} onChange={e => setBezeichnung(e.target.value)} />
            </LdFeld>
          </div>
          <LdChips aria-label="Vorschläge" wert={bezeichnung} onChange={setBezeichnung} optionen={VORSCHLAEGE.map(v => ({ wert: v, label: v }))} />
          <div className="ld-zr__aktionen" style={{ marginTop: 0 }}>
            <button type="button" className="ld-fe-knopf ld-fe-knopf--zweit" onClick={() => setOffen(false)}>Abbrechen</button>
            <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt" disabled={!datum || !bezeichnung.trim() || speichert} onClick={hinzufuegen}>{speichert ? 'Bitte warten …' : 'Tag eintragen'}</button>
          </div>
        </div>
      ) : (
        <button type="button" className="ld-zr__schnell-toggle" onClick={() => setOffen(true)}>
          <MIcon name="add" size={18} /><span>Tag hinzufügen</span>
        </button>
      )}
    </div>
  );
}
